import { useState } from 'react'
import { Menu, Calendar, User } from 'lucide-react'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { OrganizationSidebarContent } from '@/components/OrganizationSidebar'
import { PhoneNumberInput } from '@/components/PhoneNumberInput'
import { adminApi } from '@/utils/api'
import { useAuth } from '@/context/AuthContext'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'
import { Button } from '@/components/ui/button'

export default function CouplesInvitation() {
  const { user } = useAuth()
  const displayName = getUserDisplayName(user)
  const initials = getUserInitials(user)
  const [formData, setFormData] = useState({
    person1: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      gender: '',
    },
    person2: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      gender: '',
    },
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const handleInputChange = (person: 'person1' | 'person2', field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [person]: {
        ...prev[person],
        [field]: value,
      },
    }))
  }

  const handleSubmit = async () => {
    setError(null)
    setSuccess(null)

    // Validation
    if (!formData.person1.firstName || !formData.person1.lastName || !formData.person1.email || !formData.person1.phone) {
      setError('Please fill in all required fields for Person One')
      return
    }

    if (!formData.person2.firstName || !formData.person2.lastName || !formData.person2.email || !formData.person2.phone) {
      setError('Please fill in all required fields for Person Two')
      return
    }

    setIsLoading(true)

    try {
      // Transform form data to API format
      const payload: Record<string, string> = {
        personOneFirstName: formData.person1.firstName,
        personOneLastName: formData.person1.lastName,
        personOneEmail: formData.person1.email,
        personOnePhoneNumber: formData.person1.phone,
        personTwoFirstName: formData.person2.firstName,
        personTwoLastName: formData.person2.lastName,
        personTwoEmail: formData.person2.email,
        personTwoPhoneNumber: formData.person2.phone,
      }

      // Add optional fields if provided
      if (formData.person1.gender) {
        payload.personOneGender = formData.person1.gender
      }
      if (formData.person1.dateOfBirth) {
        payload.personOneDateOfBirth = formData.person1.dateOfBirth
      }
      if (formData.person2.gender) {
        payload.personTwoGender = formData.person2.gender
      }
      if (formData.person2.dateOfBirth) {
        payload.personTwoDateOfBirth = formData.person2.dateOfBirth
      }
      await adminApi.couplesInvite(payload)

      setSuccess('Invitation sent successfully! Both partners will receive the invite via WhatsApp/email.')

      // Reset form
      setFormData({
        person1: {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          dateOfBirth: '',
          gender: '',
        },
        person2: {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          dateOfBirth: '',
          gender: '',
        },
      })
    } catch (err) {
      const errorMessage = (err as Error)?.message || 'Unable to send invitation. Please try again.'
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className='min-h-screen w-full bg-[#EDEFF3]'>
      <div className='p-[27px]'>
        <div className='bg-white rounded-[24px] min-h-[calc(100vh-54px)]'>
          {/* Header */}
          <div className='px-8 py-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4'>
            <div className='flex items-center gap-4'>
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant='ghost' size='icon' className='lg:hidden -ml-2'>
                    <Menu className='h-6 w-6' />
                    <span className='sr-only'>Toggle menu</span>
                  </Button>
                </SheetTrigger>
                <SheetContent side='left' className='w-[280px] p-0'>
                  <OrganizationSidebarContent />
                </SheetContent>
              </Sheet>
              <h1 className='text-[40px] font-bold text-[#151515] leading-[70px]'>Couples Invite</h1>
            </div>
            <div className='flex items-center gap-4 lg:gap-8'>
              <div className='flex items-center gap-3'>
                <div className='w-[48px] h-[47px] bg-[#434343] rounded-full flex items-center justify-center'>
                  <span className='text-[16px] font-bold text-white'>{initials}</span>
                </div>
                <span className='text-[24px] font-bold text-[#353535] hidden sm:block'>{displayName}</span>
              </div>
              <div className='w-[41px] h-[39px] bg-[#F7F8FA] rounded-full flex items-center justify-center cursor-pointer'>
                <svg width='4' height='18' viewBox='0 0 4 18' fill='#434343'>
                  <circle cx='2' cy='2' r='2' />
                  <circle cx='2' cy='9' r='2' />
                  <circle cx='2' cy='16' r='2' />
                </svg>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className='px-8 pb-8'>
            {/* Description */}
            <p className='text-[14px] font-normal text-[#4e4d4d] leading-[22px] mb-8 max-w-full'>
              Lorem ipsum dolor sit amet consectetur. Magna cursus nunc tortor arcu velit. Cras
              quis in ultricies felis maecenas rhoncus eleifend feugiat. Sed platea venenatis
              adipiscing leo vitae aliquam. Semper lacinia bibendum est lobortis duis vulputate.
              Sit donec tristique sagittis magna dui fringilla iaculis. Dui egestas cras integer
              id neque odio montes fermentum in.
            </p>

            {/* Person One */}
            <div className='mb-8'>
              <h2 className='text-[18px] font-bold text-[#FA6017] mb-4'>Person One</h2>

              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4'>
                {/* First Name */}
                <div>
                  <label className='block text-[14px] font-medium text-[#151515] mb-2'>
                    First Name
                  </label>
                  <div className='relative'>
                    <User className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]' />
                    <input
                      type='text'
                      placeholder='Jane Doe23'
                      value={formData.person1.firstName}
                      onChange={(e) => handleInputChange('person1', 'firstName', e.target.value)}
                      className='w-full pl-10 pr-4 py-2.5 bg-white border border-[#E5E7EB] rounded-[8px] text-[14px] text-[#151515] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#FA6017] focus:border-transparent'
                    />
                  </div>
                </div>

                {/* Last Name */}
                <div>
                  <label className='block text-[14px] font-medium text-[#151515] mb-2'>
                    Last Name
                  </label>
                  <div className='relative'>
                    <User className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]' />
                    <input
                      type='text'
                      placeholder='**********'
                      value={formData.person1.lastName}
                      onChange={(e) => handleInputChange('person1', 'lastName', e.target.value)}
                      className='w-full pl-10 pr-4 py-2.5 bg-white border border-[#E5E7EB] rounded-[8px] text-[14px] text-[#151515] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#FA6017] focus:border-transparent'
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className='block text-[14px] font-medium text-[#151515] mb-2'>Email</label>
                  <div className='relative'>
                    <svg
                      className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
                      />
                    </svg>
                    <input
                      type='email'
                      placeholder='Jane.Doe23'
                      value={formData.person1.email}
                      onChange={(e) => handleInputChange('person1', 'email', e.target.value)}
                      className='w-full pl-10 pr-4 py-2.5 bg-white border border-[#E5E7EB] rounded-[8px] text-[14px] text-[#151515] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#FA6017] focus:border-transparent'
                    />
                  </div>
                </div>

                {/* Phone Number */}
                <div>
                  <label className='block text-[14px] font-medium text-[#151515] mb-2'>
                    Phone Number
                  </label>
                  <PhoneNumberInput
                    value={formData.person1.phone}
                    onChange={(value) => handleInputChange('person1', 'phone', value)}
                    placeholder='**********'
                    containerClassName='bg-white border-[#E5E7EB]'
                    inputClassName='py-0'
                    leadingIcon={
                      <svg
                        className='h-4 w-4 text-[#9CA3AF]'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z'
                        />
                      </svg>
                    }
                  />
                </div>
              </div>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                {/* Date of Birth */}
                <div>
                  <label className='block text-[14px] font-medium text-[#151515] mb-2'>
                    Date Of Birth
                  </label>
                  <div className='relative'>
                    <Calendar className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]' />
                    <input
                      type='date'
                      value={formData.person1.dateOfBirth}
                      onChange={(e) => handleInputChange('person1', 'dateOfBirth', e.target.value)}
                      className='w-full pl-10 pr-4 py-2.5 bg-white border border-[#E5E7EB] rounded-[8px] text-[14px] text-[#151515] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#FA6017] focus:border-transparent'
                    />
                  </div>
                </div>

                {/* Gender */}
                <div>
                  <label className='block text-[14px] font-medium text-[#151515] mb-2'>
                    Gender
                  </label>
                  <select
                    value={formData.person1.gender}
                    onChange={(e) => handleInputChange('person1', 'gender', e.target.value)}
                    className='w-full px-4 py-2.5 bg-white border border-[#E5E7EB] rounded-[8px] text-[14px] text-[#151515] focus:outline-none focus:ring-2 focus:ring-[#FA6017] focus:border-transparent'
                  >
                    <option value=''>Select gender</option>
                    <option value='male'>Male</option>
                    <option value='female'>Female</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Person Two */}
            <div className='mb-8'>
              <h2 className='text-[18px] font-bold text-[#FA6017] mb-4'>Person Two</h2>

              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4'>
                {/* First Name */}
                <div>
                  <label className='block text-[14px] font-medium text-[#151515] mb-2'>
                    First Name
                  </label>
                  <div className='relative'>
                    <User className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]' />
                    <input
                      type='text'
                      placeholder='Jane Doe23'
                      value={formData.person2.firstName}
                      onChange={(e) => handleInputChange('person2', 'firstName', e.target.value)}
                      className='w-full pl-10 pr-4 py-2.5 bg-white border border-[#E5E7EB] rounded-[8px] text-[14px] text-[#151515] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#FA6017] focus:border-transparent'
                    />
                  </div>
                </div>

                {/* Last Name */}
                <div>
                  <label className='block text-[14px] font-medium text-[#151515] mb-2'>
                    Last Name
                  </label>
                  <div className='relative'>
                    <User className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]' />
                    <input
                      type='text'
                      placeholder='**********'
                      value={formData.person2.lastName}
                      onChange={(e) => handleInputChange('person2', 'lastName', e.target.value)}
                      className='w-full pl-10 pr-4 py-2.5 bg-white border border-[#E5E7EB] rounded-[8px] text-[14px] text-[#151515] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#FA6017] focus:border-transparent'
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className='block text-[14px] font-medium text-[#151515] mb-2'>Email</label>
                  <div className='relative'>
                    <svg
                      className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
                      />
                    </svg>
                    <input
                      type='email'
                      placeholder='Jane.Doe23'
                      value={formData.person2.email}
                      onChange={(e) => handleInputChange('person2', 'email', e.target.value)}
                      className='w-full pl-10 pr-4 py-2.5 bg-white border border-[#E5E7EB] rounded-[8px] text-[14px] text-[#151515] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#FA6017] focus:border-transparent'
                    />
                  </div>
                </div>

                {/* Phone Number */}
                <div>
                  <label className='block text-[14px] font-medium text-[#151515] mb-2'>
                    Phone Number
                  </label>
                  <PhoneNumberInput
                    value={formData.person2.phone}
                    onChange={(value) => handleInputChange('person2', 'phone', value)}
                    placeholder='**********'
                    containerClassName='bg-white border-[#E5E7EB]'
                    inputClassName='py-0'
                    leadingIcon={
                      <svg
                        className='h-4 w-4 text-[#9CA3AF]'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z'
                        />
                      </svg>
                    }
                  />
                </div>
              </div>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                {/* Date of Birth */}
                <div>
                  <label className='block text-[14px] font-medium text-[#151515] mb-2'>
                    Date Of Birth
                  </label>
                  <div className='relative'>
                    <Calendar className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]' />
                    <input
                      type='date'
                      value={formData.person2.dateOfBirth}
                      onChange={(e) => handleInputChange('person2', 'dateOfBirth', e.target.value)}
                      className='w-full pl-10 pr-4 py-2.5 bg-white border border-[#E5E7EB] rounded-[8px] text-[14px] text-[#151515] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#FA6017] focus:border-transparent'
                    />
                  </div>
                </div>

                {/* Gender */}
                <div>
                  <label className='block text-[14px] font-medium text-[#151515] mb-2'>
                    Gender
                  </label>
                  <select
                    value={formData.person2.gender}
                    onChange={(e) => handleInputChange('person2', 'gender', e.target.value)}
                    className='w-full px-4 py-2.5 bg-white border border-[#E5E7EB] rounded-[8px] text-[14px] text-[#151515] focus:outline-none focus:ring-2 focus:ring-[#FA6017] focus:border-transparent'
                  >
                    <option value=''>Select gender</option>
                    <option value='male'>Male</option>
                    <option value='female'>Female</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Error/Success Messages */}
            {(error || success) && (
              <div className={`mb-6 p-4 rounded-[8px] ${error ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                <p className={`text-[14px] ${error ? 'text-red-800' : 'text-green-800'}`}>
                  {error || success}
                </p>
              </div>
            )}

            {/* Submit Button */}
            <div className='flex justify-center'>
              <button
                type='button'
                onClick={handleSubmit}
                disabled={isLoading}
                className='px-8 py-3 bg-[#151515] text-white font-semibold text-[14px] rounded-[8px] hover:bg-[#2a2a2a] transition-colors focus:outline-none focus:ring-2 focus:ring-[#151515] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {isLoading ? 'Sending...' : 'Send Invite'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
